import React from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';
import { Box, Typography, useTheme } from '@mui/material';

import { font } from '../../utils/fontHelper';

interface SectionHeaderWithChevronProps {
  title: string;
  subtitle?: string;
  onClick?: () => void;
  size?: 'medium' | 'small' | 'xsmall';
  showChevron?: boolean;
  sx?: object;
}

const SectionHeaderWithChevron: React.FC<SectionHeaderWithChevronProps> = ({
  title,
  subtitle,
  onClick,
  size = 'medium',
  showChevron = true,
  sx = {}
}) => {
  const theme = useTheme();

  // Determine typography variant based on size
  const variant = size === 'medium' ? 'h6' : size === 'xsmall' ? 'body2' : 'h7';

  // Size-specific styles
  const titleStyles =
    size === 'medium'
      ? {
          fontWeight: 700,
          fontSize: { xs: '1.125rem', sm: '1.5rem' },
        }
      : size === 'xsmall'
        ? {
            fontWeight: 600,
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            letterSpacing: '0.02em',
          }
        : {
            fontWeight: 700,
            fontSize: { xs: '1.125rem', sm: '1.25rem' }, // h7 fontSize is already defined in theme (1.125rem / 18px)
          };

  const chevronSize = size === 'medium' ? 16 : size === 'xsmall' ? 14 : 16;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: subtitle ? 'column' : 'row',
        alignItems: subtitle ? 'flex-start' : 'center',
        gap: subtitle ? 0 : 0.5,
        cursor: onClick ? 'pointer' : 'default',
        mb: size === 'xsmall' ? 0.75 : size === 'medium' ? 1.5 : 1,
        transition: 
        'opacity 0.2s ease',
        '&:hover': onClick ? {
          opacity: 0.7,
        } : {},
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography
          variant={variant}
          sx={{
            ...titleStyles,
            fontFamily: font(),
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {showChevron && onClick && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: '2px', transition: 'transform 0.2s ease', '&:hover': { transform: 'translateX(2px)' } }}>
            <CaretRightIcon
              size={chevronSize}
              color={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
            />
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(0, 0, 0, 0.4)',
            fontSize: 14,
            mt: 0.25,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default SectionHeaderWithChevron;
